// src/services/geoService.js
// Servicio para Países, Departamentos y Municipios en cascada
// - Usa axiosInstance (mismo patrón que tus otros services)
// - Incluye caché en memoria con TTL
// - Helpers para mapear a opciones de react-select

import axiosInstance from "../config/axiosInstance";

/** Base de los endpoints del GeoController */
const BASE_URL = "geo";

/** TTL del caché (ms). Por defecto 24 horas como en backend */
const DEFAULT_TTL = 24 * 60 * 60 * 1000;

/** Caché en memoria por clave */
const _cache = new Map();

/**
 * Obtiene (o setea) un valor del caché con TTL simple.
 * @param {string} key
 * @param {() => Promise<any>} fetcher - Función que trae el valor si no está en caché o expiró.
 * @param {number} ttlMs - Tiempo en ms para expirar.
 * @returns {Promise<any>}
 */
async function getWithCache(key, fetcher, ttlMs = DEFAULT_TTL) {
  const now = Date.now();
  const cached = _cache.get(key);
  if (cached && cached.expiresAt > now) {
    return cached.value;
  }
  const value = await fetcher();
  _cache.set(key, { value, expiresAt: now + ttlMs });
  return value;
}

/** Limpia el caché completo o por prefijo */
export function clearGeoCache(prefix = null) {
  if (!prefix) {
    _cache.clear();
    return;
  }
  for (const key of _cache.keys()) {
    if (key.startsWith(prefix)) _cache.delete(key);
  }
}

/** Util: mapea lista {id, name} a opciones de react-select [{value,label}] */
export function toSelectOptions(list = [], valueKey = "id", labelKey = "name") {
  return list.map((x) => ({
    value: x[valueKey],
    label: x[labelKey],
  }));
}

/** ------------------ ENDPOINTS CRUD (Lectura) ------------------ */

/**
 * Lista todos los países.
 * GET /api/geo/countries
 * @returns {Promise<ApiResponse<CountryDto[]>>}
 */
export async function getCountries({ useCache = true } = {}) {
  const key = "geo:countries";
  const fetcher = async () => {
    const { data } = await axiosInstance.get(`${BASE_URL}/countries`);
    return data; // ApiResponse<List<CountryDto>>
  };
  return useCache ? getWithCache(key, fetcher) : fetcher();
}

/**
 * Lista departamentos por país.
 * GET /api/geo/countries/{countryId}/departments
 * @param {string} countryId - GUID del país
 * @returns {Promise<ApiResponse<DepartmentDto[]>>}
 */
export async function getDepartmentsByCountry(
  countryId,
  { useCache = true } = {}
) {
  if (!countryId) throw new Error("countryId es requerido.");
  const key = `geo:departments:${countryId}`;
  const fetcher = async () => {
    const { data } = await axiosInstance.get(
      `${BASE_URL}/countries/${countryId}/departments`
    );
    return data; // ApiResponse<List<DepartmentDto>>
  };
  return useCache ? getWithCache(key, fetcher) : fetcher();
}

/**
 * Lista municipios por departamento.
 * GET /api/geo/departments/{departmentId}/municipalities
 * @param {string} departmentId - GUID del departamento
 * @returns {Promise<ApiResponse<MunicipalityDto[]>>}
 */
export async function getMunicipalitiesByDepartment(
  departmentId,
  { useCache = true } = {}
) {
  if (!departmentId) throw new Error("departmentId es requerido.");
  const key = `geo:municipalities:${departmentId}`;
  const fetcher = async () => {
    const { data } = await axiosInstance.get(
      `${BASE_URL}/departments/${departmentId}/municipalities`
    );
    return data; // ApiResponse<List<MunicipalityDto>>
  };
  return useCache ? getWithCache(key, fetcher) : fetcher();
}

/** ------------------ HELPERS PARA SELECTS ------------------ */

/**
 * Trae países y los mapea a opciones de react-select.
 * @returns {Promise<{options: {value:string,label:string}[], raw: ApiResponse<any>}>}
 */
export async function getCountryOptions() {
  const resp = await getCountries();
  const items = resp?.data ?? [];
  return { options: toSelectOptions(items), raw: resp };
}

/**
 * Trae departamentos por país y los mapea a opciones de react-select.
 * @param {string} countryId
 * @returns {Promise<{options: {value:string,label:string}[], raw: ApiResponse<any>}>}
 */
export async function getDepartmentOptions(countryId) {
  const resp = await getDepartmentsByCountry(countryId);
  const items = resp?.data ?? [];
  return { options: toSelectOptions(items), raw: resp };
}

/**
 * Trae municipios por departamento y los mapea a opciones de react-select.
 * @param {string} departmentId
 * @returns {Promise<{options: {value:string,label:string}[], raw: ApiResponse<any>}>}
 */
export async function getMunicipalityOptions(departmentId) {
  const resp = await getMunicipalitiesByDepartment(departmentId);
  const items = resp?.data ?? [];
  return { options: toSelectOptions(items), raw: resp };
}

/** ------------------ Tipos (JSDoc) ------------------ */

/**
 * @typedef {Object} CountryDto
 * @property {string} id
 * @property {string} iso2
 * @property {string} iso3
 * @property {number} isoNumeric
 * @property {string} name
 */

/**
 * @typedef {Object} DepartmentDto
 * @property {string} id
 * @property {string} countryId
 * @property {string} code
 * @property {string} name
 */

/**
 * @typedef {Object} MunicipalityDto
 * @property {string} id
 * @property {string} departmentId
 * @property {string} code
 * @property {string} name
 */

/**
 * @template T
 * @typedef {Object} ApiResponse
 * @property {boolean} success
 * @property {number=} code
 * @property {string|null=} message
 * @property {T} data
 */
